import { useQuery, keepPreviousData } from "@tanstack/react-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";
import { getMasterAPI, getMasterListRowCountAPI, getMasterSettingsAPI } from "@/core/api/api.service";
import { CommonApiResponseType } from "@/types/api";
import { SettingsMetadata } from "@/shared/components/ui/table/type";

// Define the props type
interface UseGetMasterProps {
    docType?: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    sorting: MRT_SortingState;
    pagination: MRT_PaginationState;
    extraFilters?: any;
    tab?: string;
    listFn?: any;
    docNo?: string;
    enabled?: boolean; // Optional parameter to control if the query should run
    skipSettings?: boolean; // Optional parameter to skip settings API call
    staleTime?: number;
    refetchInterval?: number;
}

export function useGetMaster({ docType, columnFilters, globalFilter, sorting, pagination, extraFilters, tab, listFn, docNo, enabled, staleTime, refetchInterval }: UseGetMasterProps) {
    const query = useQuery<{ data: CommonApiResponseType<any>, settings: any }>({
        queryKey: [
            'master',
            docType,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
            extraFilters,
            tab,
            listFn,
            docNo,
        ],
        queryFn: async () => {
            const params: Record<string, any> = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                globalFilter,
                sorting: JSON.stringify(sorting),
                extraFilters: JSON.stringify(extraFilters ?? {}),
                listFn,
                docNo,
            };

            if (tab !== undefined) {
                params.tab = tab;
            }

            const queryString = new URLSearchParams(
                Object.entries(params)
                    .filter(([_, v]) => v !== undefined && v !== null)
                    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {} as Record<string, string>)
            ).toString();

            let settings = null;
            settings = await getMasterSettingsAPI(docType ?? "", queryString) as unknown as SettingsMetadata;
            const data = await getMasterAPI(docType ?? "", queryString);
            return {
                data: data,
                settings: settings,
            }
        },
        placeholderData: keepPreviousData,
        staleTime: staleTime ?? 0,
        refetchInterval: refetchInterval,
        enabled: enabled !== undefined ? enabled : !!docType, // Use provided enabled value or fallback to !!docType
        retry: 1,
    });

    return { ...query };
}


export function useGetMasterSettings(docType: string | undefined) {
    const query = useQuery<any>({
        queryKey: ['masterSettings', docType],
        queryFn: async () => {
            if (!docType) throw new Error("docType is required");
            const data = await getMasterSettingsAPI(docType) as unknown as any;
            return { data: data };
        },
        enabled: !!docType,
        retry: 1,
    });

    return query;
}


export function useGetMasterListRowCount({ docType, columnFilters, globalFilter, sorting, pagination, extraFilters, tab, listFn, docNo, enabled }: UseGetMasterProps) {
    const query = useQuery<{ data: CommonApiResponseType<any> }>({
        queryKey: [
            'masterListRowCount',
            docType,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
            extraFilters,
            tab,
            listFn,
            docNo,
        ],
        queryFn: async () => {
            const params: Record<string, any> = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                globalFilter,
                sorting: JSON.stringify(sorting),
                extraFilters: JSON.stringify(extraFilters ?? {}),
                listFn,
                docNo,
            };

            if (tab !== undefined) {
                params.tab = tab;
            }

            const queryString = new URLSearchParams(
                Object.entries(params)
                    .filter(([_, v]) => v !== undefined && v !== null)
                    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {} as Record<string, string>)
            ).toString();

            const data = await getMasterListRowCountAPI(docType ?? "", queryString);
            return {
                data: data,
            }
        },
        placeholderData: keepPreviousData,
        enabled: enabled !== undefined ? enabled : !!docType, // Use provided enabled value or fallback to !!docType
        retry: 1,
    });

    return { ...query };
}

